import { useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import MerchantContext from "../contexts/MerchantContext";
import { trpc } from "../trpc";
import type { RouterOutputs } from "@/trpc";

type SystemTemplateKind = RouterOutputs["whatsappTemplates"]["systemTemplateKinds"][number];
type TemplateRole = RouterOutputs["whatsappTemplates"]["list"]["templates"][number]["role"];

type PromoteTarget = {
  id: string;
  name: string;
  // `systemId` (the routing key on gsi1) is the source of truth for "is this a system
  // template" — not the drift-prone `isSystem` boolean.
  systemId?: string;
  role: TemplateRole;
};

type PromoteToSystemModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  template: PromoteTarget | null;
  // Edit mode only: hand the demote confirmation back to the container's AlertDialog.
  onRequestDemote: () => void;
};

// Merchant-facing label for the role a system template currently serves.
const roleLabel = (role: TemplateRole): string => {
  if (!role) return "Sistema";
  return role.scopePrefix ? `${role.label} · ${role.scopePrefix}` : role.label;
};

// Radix Select rejects empty-string values, so "global" fallback within a project/pipeline
// picker uses a sentinel translated on read/write.
const GLOBAL = "__global__";

// Build the discriminated scope payload the procedure expects from the chosen kind + value.
const buildScope = (
  kind: SystemTemplateKind,
  scopeValue: string
):
  | { type: "global" }
  | { type: "project"; projectId: string }
  | { type: "pipeline"; pipelineId: string } => {
  if (kind.scope === "global") return { type: "global" };
  if (scopeValue === GLOBAL) return { type: "global" };
  if (kind.scope === "project") return { type: "project", projectId: scopeValue };
  return { type: "pipeline", pipelineId: scopeValue };
};

const PromoteToSystemModal = ({
  isOpen,
  onClose,
  onSuccess,
  template,
  onRequestDemote,
}: PromoteToSystemModalProps) => {
  const { merchantId } = useContext(MerchantContext);
  const utils = trpc.useUtils();

  // A template that is already a system template is being re-assigned or removed.
  const isEditing = !!template?.systemId;

  const [kindKey, setKindKey] = useState<string>("");
  const [scopeValue, setScopeValue] = useState<string>("");
  const [isChecking, setIsChecking] = useState(false);
  const [conflict, setConflict] = useState<{ id: string; name: string } | null>(null);

  const { data: kinds, isLoading: kindsLoading } =
    trpc.whatsappTemplates.systemTemplateKinds.useQuery(
      { merchantId },
      { enabled: !!merchantId && isOpen }
    );

  const { data: projects } = trpc.projects.list.useQuery(
    { merchantId },
    { enabled: !!merchantId && isOpen }
  );

  const { data: pipelines } = trpc.pipeline.list.useQuery(
    { merchantId },
    { enabled: !!merchantId && isOpen }
  );

  // The "auth" system pipeline (AUTH_PIPELINE_ID) holds OTP-delivery leads and is never a
  // valid promote target, so it must not appear in the embudo selector.
  const selectablePipelines = useMemo(
    () => (pipelines ?? []).filter((pipeline) => pipeline.id !== "auth"),
    [pipelines]
  );

  const selectedKind = useMemo(
    () => (kinds ?? []).find((k) => k.key === kindKey) ?? null,
    [kinds, kindKey]
  );

  const promote = trpc.whatsappTemplates.promoteToSystem.useMutation({
    onSuccess: (result) => {
      utils.whatsappTemplates.list.invalidate();
      toast.success("Plantilla del sistema actualizada", {
        description: result.demoted
          ? `Se reemplazó «${result.demoted.name}» en este rol.`
          : `«${result.template.name}» ahora es una plantilla del sistema.`,
      });
      onSuccess();
      handleClose();
    },
    onError: (error) => {
      toast.error("No se pudo promover la plantilla", {
        description: error.message,
      });
    },
  });

  const handleClose = () => {
    setKindKey("");
    setScopeValue("");
    setConflict(null);
    setIsChecking(false);
    onClose();
  };

  // A scope selection is complete when: global kind (no picker), or a value is chosen, or a
  // non-required kind may fall back to global.
  const scopeReady =
    !selectedKind ||
    selectedKind.scope === "global" ||
    scopeValue !== "" ||
    !selectedKind.scopeRequired;

  const canSubmit = !!template && !!selectedKind && scopeReady && !isChecking;

  const runPromotion = (scope: ReturnType<typeof buildScope>) => {
    if (!template || !selectedKind) return;
    promote.mutate({ merchantId, id: template.id, kindKey: selectedKind.key, scope });
  };

  const handleSubmit = async () => {
    if (!template || !selectedKind) return;
    const scope = buildScope(selectedKind, scopeValue);
    setIsChecking(true);
    try {
      const preview = await utils.whatsappTemplates.previewPromotion.fetch({
        merchantId,
        id: template.id,
        kindKey: selectedKind.key,
        scope,
      });
      if (preview.conflict) {
        setConflict(preview.conflict);
        return;
      }
      runPromotion(scope);
    } catch (error) {
      toast.error("No se pudo validar la promoción", {
        description: error instanceof Error ? error.message : "Error desconocido",
      });
    } finally {
      setIsChecking(false);
    }
  };

  const confirmReplace = () => {
    if (!selectedKind) return;
    setConflict(null);
    runPromotion(buildScope(selectedKind, scopeValue));
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="h-full max-h-full w-full md:h-auto md:max-h-[85vh] md:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Editar plantilla del sistema" : "Usar como plantilla del sistema"}
            </DialogTitle>
            <DialogDescription>
              {template
                ? isEditing
                  ? `«${template.name}» se usa actualmente en: ${roleLabel(template.role)}. Elige un nuevo momento o quítala del sistema.`
                  : `Elige para qué momento automatizado usará el sistema «${template.name}».`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {kindsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="flex flex-col gap-4 py-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="system-kind">Tipo de plantilla del sistema</Label>
                <Select
                  value={kindKey}
                  onValueChange={(value) => {
                    setKindKey(value);
                    setScopeValue("");
                  }}
                >
                  <SelectTrigger id="system-kind" className="min-h-11">
                    <SelectValue placeholder="Selecciona un tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {(kinds ?? []).map((kind) => (
                      <SelectItem key={kind.key} value={kind.key}>
                        {kind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedKind && (
                  <p className="text-sm text-gray-500">{selectedKind.description}</p>
                )}
              </div>

              {selectedKind?.scope === "project" && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="system-project">Proyecto</Label>
                  <Select value={scopeValue} onValueChange={setScopeValue}>
                    <SelectTrigger id="system-project" className="min-h-11">
                      <SelectValue placeholder="Selecciona un proyecto" />
                    </SelectTrigger>
                    <SelectContent>
                      {!selectedKind.scopeRequired && (
                        <SelectItem value={GLOBAL}>Todos los proyectos (global)</SelectItem>
                      )}
                      {(projects ?? []).map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedKind?.scope === "pipeline" && (
                <div className="flex flex-col gap-2">
                  <Label htmlFor="system-pipeline">Embudo</Label>
                  <Select value={scopeValue} onValueChange={setScopeValue}>
                    <SelectTrigger id="system-pipeline" className="min-h-11">
                      <SelectValue placeholder="Selecciona un embudo" />
                    </SelectTrigger>
                    <SelectContent>
                      {!selectedKind.scopeRequired && (
                        <SelectItem value={GLOBAL}>Todos los embudos (global)</SelectItem>
                      )}
                      {selectablePipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col gap-2 md:flex-row md:justify-between">
            {isEditing ? (
              <Button
                variant="outline"
                onClick={() => {
                  handleClose();
                  onRequestDemote();
                }}
                className="min-h-11 w-full border-red-300 text-red-600 hover:bg-red-50 md:w-auto"
              >
                Quitar del sistema
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleClose}
                className="min-h-11 w-full md:w-auto"
              >
                Cancelar
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || promote.isLoading}
              className="min-h-11 w-full bg-green-500 text-white hover:bg-green-600 md:w-auto"
            >
              {isChecking || promote.isLoading ? (
                <Spinner size="sm" />
              ) : isEditing ? (
                "Guardar cambios"
              ) : (
                "Usar como plantilla del sistema"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!conflict} onOpenChange={(open) => !open && setConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reemplazar plantilla del sistema</AlertDialogTitle>
            <AlertDialogDescription>
              {conflict
                ? `Esta plantilla reemplazará a «${conflict.name}» en este rol; los mensajes automáticos usarán la nueva. La anterior se conservará y podrás volver a usarla.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-11">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmReplace}
              className="min-h-11 bg-green-500 text-white hover:bg-green-600"
            >
              Reemplazar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PromoteToSystemModal;
